import { ScanBarcode, X } from "lucide-react";
import * as React from "react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { useSearchEnd, useWorkSearch } from "../../data/catalog";
import { useShelf } from "../../data/library";
import { useScrollLock } from "../../hooks/useScrollLock";
import { useVisualViewport } from "../../hooks/useVisualViewport";
import { creatorNames } from "../../lib/credits";
import { progressVerb } from "../../lib/media";
import Work from "../../types/Work";
import SearchInput from "../ui/SearchInput";
import WorkThumb from "../works/WorkThumb";

type Props = { onClose: () => void };

/**
 * Thumb-reachable "quick log" sheet (ADR 0005): opened from the bottom nav,
 * it surfaces what the user is currently reading/watching/playing and a
 * catalog search. Picking anything opens the work modal, where the full
 * logging flow (TrackingPanel) lives.
 */
const QuickLog: React.FC<Props> = ({ onClose }) => {
  useScrollLock();
  const visible = useVisualViewport();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");

  const { data: inProgressData } = useShelf(
    user ? { status: "in_progress" } : null,
  );
  const inProgress = inProgressData ?? [];

  // Signed in, for the types you track (ADR 0044).
  const { data: searchData } = useWorkSearch(query.trim() || null, 8, 200);
  const results = searchData ?? [];
  // How the search ended (plan 014's 6d), the words never sent.
  const ended = useSearchEnd("quicklog", query, searchData?.length);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const open = (workId: number) => {
    onClose();
    navigate(`/works/${workId}`, { state: { background: location } });
  };

  const scan = () => {
    ended.handedOff();
    onClose();
    navigate("/scan");
  };

  // A result's `rank`, from 1; Continue's rows have none.
  const row = (work: Work, suffix?: string, rank?: number) => {
    const sub = suffix || creatorNames(work);
    const pick = () => {
      if (rank !== undefined) ended.picked(rank);
      open(work.id);
    };
    return (
      <button key={work.id} className="sheet-row" onClick={pick}>
        <WorkThumb work={work} className="sheet-row-icon" />
        <span className="sheet-row-main">
          <span className="sheet-row-title">{work.title}</span>
          {sub && <span className="sheet-row-sub muted">{sub}</span>}
        </span>
      </button>
    );
  };

  const showInProgress = !query.trim() && inProgress.length > 0;

  return (
    // The scrim is a pointer's way out; Escape and Close are the keyboard's.
    <div
      className="sheet-backdrop"
      style={visible}
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <dialog open className="sheet" aria-label="Quick log">
        <div className="sheet-head">
          <h3>Quick log</h3>
          <button
            className="btn btn-ghost btn-sm icon-only"
            aria-label="Close"
            onClick={onClose}
          >
            <X size="1em" aria-hidden="true" />
          </button>
        </div>

        <SearchInput
          placeholder="Search anything…"
          autoFocus
          value={query}
          onChange={setQuery}
        />

        <button className="btn sheet-scan" onClick={scan}>
          <ScanBarcode size="1em" aria-hidden="true" /> Scan a book barcode
        </button>

        {showInProgress && (
          <div className="sheet-section">
            <div className="sheet-section-label muted">Continue</div>
            {inProgress
              .filter((entry) => entry.work)
              .map((entry) =>
                row(
                  entry.work as Work,
                  progressVerb((entry.work as Work).workType),
                ),
              )}
          </div>
        )}

        {results.length > 0 && (
          <div className="sheet-section">
            <div className="sheet-section-label muted">Catalog</div>
            {results.map((work, i) => row(work, undefined, i + 1))}
          </div>
        )}
      </dialog>
    </div>
  );
};

export default QuickLog;
